import { createRoute, retainSearchParams } from "@tanstack/react-router";

import { isUuid, signedInRoute } from "../../platform/routes";
import { type FrameSearch, frameSearch } from "../../platform/ui-switch/frame";
import { StaffAiTasksScreen } from "./StaffAiTasksScreen";
import { StaffAuditScreen } from "./StaffAuditScreen";
import { requireStaff } from "./StaffGate";
import { StaffGenerationsScreen } from "./StaffGenerationsScreen";
import { StaffLayout } from "./StaffLayout";
import { StaffTeamScreen } from "./StaffTeamScreen";
import { StaffTeamsScreen } from "./StaffTeamsScreen";
import { StaffTraceScreen } from "./StaffTraceScreen";
import { StaffUsageScreen } from "./StaffUsageScreen";
import { generationsSearch, usageSearch } from "./usage-search";

/**
 * `/staff`: staff only, by `Me.is_staff`; anyone else gets not-found, as the API answers. In the
 * frame of the team the user was on, as live's Admin Panel: Classic's, or Pro's or Simple's when
 * `?ui=` names one, which every link to a staff page keeps (spec ui-perf/admin-panel-pro-simple).
 */
export const staffRoute = createRoute({
  getParentRoute: () => signedInRoute,
  path: "staff",
  staticData: { teamFrame: true },
  validateSearch: (search) => frameSearch.parse(search),
  search: { middlewares: [retainSearchParams<FrameSearch>(["ui"])] },
  beforeLoad: ({ context }) => requireStaff(context.queryClient),
  component: StaffLayout,
});

export const staffTeamsRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/",
  component: StaffTeamsScreen,
});

export const staffTeamRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "teams/$teamId",
  params: { parse: ({ teamId }) => (isUuid(teamId) ? { teamId } : false) },
  component: StaffTeamScreen,
});

/** G9's "AI tasks": every fixed-model step and the model it runs on (issue 433). */
export const staffAiTasksRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "ai-tasks",
  component: StaffAiTasksScreen,
});

/** Usage per user, team, brand, model, type or day (spec platform/usage-analytics.md 3). */
export const staffUsageRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "usage",
  validateSearch: (search) => usageSearch.parse(search),
  component: StaffUsageScreen,
});

/** The generations behind one row of Usage. */
export const staffGenerationsRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "usage/generations",
  validateSearch: (search) => generationsSearch.parse(search),
  component: StaffGenerationsScreen,
});

/** One job's trace, under its team as the API reads it: each opening is an audited read. */
export const staffTraceRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "teams/$teamId/jobs/$jobId",
  params: {
    parse: ({ teamId, jobId }) => (isUuid(teamId) && isUuid(jobId) ? { teamId, jobId } : false),
  },
  component: StaffTraceScreen,
});

export const staffAuditRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "audit",
  component: StaffAuditScreen,
});
